import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BaseComponent } from '@stockaid/core';
import { first } from 'rxjs/operators';
import { SnotifyService } from 'ng-snotify';
import { roundFloatNumber } from '@stockaid/utils';
import {
  computeNewMinMax,
  getDerivedSalePrice,
  getRepricingEffectiveTrend,
  validateDerivedSalePrice,
  validateRepricingPrice,
  validateRepricingBounds,
} from 'src/app/core/utils/recommendation.utils';
import {
  ApplyRecommendationPayload,
  RecommendationEntityType,
  RecommendationType,
} from 'src/app/core/models/recommendation';
import { RepricingItem } from 'src/app/core/utils/recommendation.utils';
import { RecommendationsService } from 'src/app/core/services/recommendation.service';
import { COMMON_FORMAT_DATE } from 'src/app/core/constants/format-date.constant';
import { RecommendationStatus } from 'src/app/core/infrastructure/enums/meta-data-field.enum';
import { ItemPriceState } from 'src/app/core/models/history-log';
import Swal from 'sweetalert2';
import { ItemPricingRecommendation } from 'src/app/core/models/item-pricing-recommendation';

@Component({
  selector: 'app-repricing-setting-modal',
  templateUrl: './repricing-setting-modal.component.html',
  styleUrls: ['./repricing-setting-modal.component.scss'],
})
export class RepricingSettingModalComponent extends BaseComponent {
  @Input() item!: ItemPricingRecommendation;
  @Input() displayCurrency!: { code: string; symbol: string };
  @Input() isLoading: boolean = true;
  @Input() isSubmitting: boolean = false;
  @Input() warningMessage: string = '';
  @Input() status: RecommendationStatus;

  private _repricingItem!: RepricingItem;
  @Input() set repricingItem(value: RepricingItem) {
    this._repricingItem = value;
    if (value && this.customPrice === null) {
      this.customPrice = value.customPrice ?? value.newInboundPrice ?? value.currentInboundPrice ?? null;
    }
    if (value && this.customMin === null) {
      this.customMin = value.customMinPrice != null ? value.customMinPrice : (value.suggestedMin != null ? value.suggestedMin : (value.currentMin != null ? value.currentMin : null));
    }
    if (value && this.customMax === null) {
      this.customMax = value.customMaxPrice != null ? value.customMaxPrice : (value.suggestedMax != null ? value.suggestedMax : (value.currentMax != null ? value.currentMax : null));
    }
    if (value?.state === ItemPriceState.MISSING_PRICE) {
      this.isEditing = true;
    }
  }
  get repricingItem(): RepricingItem {
    return this._repricingItem;
  }

  // Manual overridden price state & validation
  public customPrice: number | null = null;
  public customMin: number | null = null;
  public customMax: number | null = null;
  public isEditing: boolean = false;
  public isEditingMin: boolean = false;
  public isEditingMax: boolean = false;

  readonly RecommendationStatus = RecommendationStatus;
  readonly ItemPriceState = ItemPriceState;
  readonly FORMAT_DATE = COMMON_FORMAT_DATE;

  constructor(
    public activeModal: NgbActiveModal,
    private recommendationsService: RecommendationsService,
    private snotifyService: SnotifyService
  ) {
    super();
  }


  get isNoChanges() {
    return this.repricingItem?.currentInboundPrice === this.customPrice
      && (this.customMin == null || this.customMin === this.repricingItem?.currentMin)
      && (this.customMax == null || this.customMax === this.repricingItem?.currentMax);
  }

  get isSyncDisabled() {
    return [
      RecommendationStatus.QUEUED,
      RecommendationStatus.IN_PROGRESS,
      RecommendationStatus.APPLIED,
    ].includes(this.status);
  }

  get isWarning() {
    return Boolean(this.warningMessage);
  }

  get isExceedRange(): boolean {
    return (
      this.repricingItem?.newInboundPrice !==
      this.repricingItem?.calculatedInboundPrice
    );
  }

  get validationError(): string | null {
    const inboundErr = validateRepricingPrice(
      this.customPrice,
      {
        min: this.liveMinMax.newMin,
        max: this.liveMinMax.newMax,
        listPrice: this.repricingItem?.currentInboundPrice,
      },
      (v) => this.getDisplayCurrency(v)
    );
    if (inboundErr) return inboundErr;

    if (this.repricingItem?.isSaleActive && this.customPrice != null) {
      const derivedSale = this.getDerivedSalePrice();
      return validateDerivedSalePrice(
        derivedSale,
        { min: this.liveMinMax.newMin, max: this.liveMinMax.newMax },
        (v) => this.getDisplayCurrency(v)
      );
    }

    return null;
  }

  get minValidationError(): string | null {
    return validateRepricingBounds(
      this.customMin, this.customMax, this.customPrice,
      this.repricingItem?.currentInboundPrice ?? null,
      (v) => this.getDisplayCurrency(v),
      this.repricingItem?.currentMax ?? null,
    ).minError;
  }

  get maxValidationError(): string | null {
    return validateRepricingBounds(
      this.customMin, this.customMax, this.customPrice,
      this.repricingItem?.currentInboundPrice ?? null,
      (v) => this.getDisplayCurrency(v),
      this.repricingItem?.currentMax ?? null,
    ).maxError;
  }

  get minMaxValidationError(): string | null {
    return this.minValidationError || this.maxValidationError;
  }

  get liveMinMax(): { newMin: number | undefined; newMax: number | undefined } {
    // If user set custom min/max, use them directly (overrides computed values)
    if (this.customMin != null || this.customMax != null) {
      return {
        newMin: this.customMin ?? this.repricingItem?.currentMin,
        newMax: this.customMax ?? this.repricingItem?.currentMax,
      };
    }
    const activePrice = this.customPrice ?? this.repricingItem?.newInboundPrice;
    if (!activePrice) return { newMin: this.repricingItem?.currentMin, newMax: this.repricingItem?.currentMax };
    return computeNewMinMax(
      activePrice,
      this.repricingItem?.suggestedMin,
      this.repricingItem?.suggestedMax,
      this.repricingItem?.currentMin,
      this.repricingItem?.currentMax,
    );
  }

  public openEditMin(): void { this.isEditingMin = true; }
  public commitEditMin(): void { this.isEditingMin = false; }
  public resetCustomMin(): void {
    this.customMin = this.repricingItem?.currentMin ?? null;
    this.isEditingMin = false;
  }

  public openEditMax(): void { this.isEditingMax = true; }
  public commitEditMax(): void { this.isEditingMax = false; }
  public resetCustomMax(): void {
    // reset to suggested Max when click revert button in edit mode
    this.customMax = this.repricingItem?.suggestedMax ?? null;
    this.isEditingMax = false;
  }

  public onCancel() {
    this.activeModal.dismiss();
  }

  public onSyncToAmazon(e: Event) {
    if (this.isSubmitting || this.isLoading || !this.item || this.isNoChanges || this.customPrice == null || (this.isEditing && this.validationError) || this.minMaxValidationError) {
      return;
    }

    const fmt = (v: number | null | undefined) => v != null ? this.getDisplayCurrency(v) : 'N/A';
    const priceStr = fmt(this.customPrice);
    const minStr = fmt(this.customMin ?? this.liveMinMax.newMin);
    const maxStr = fmt(this.customMax ?? this.liveMinMax.newMax);

    Swal.fire({
      title: 'Confirm Price Update',
      html: `<div style="text-align:left;font-size:14px;">
        <p style="margin-bottom:12px;">The following prices will be synced to Amazon for <strong>${this.item.sku}</strong>:</p>
        <table style="width:100%;border-collapse:collapse;">
          <tr><td style="padding:6px 12px;color:#666;width:40%;">Your Price</td><td style="padding:6px 12px;font-weight:700;">${priceStr}</td></tr>
          <tr><td style="padding:6px 12px;color:#666;">Min Price</td><td style="padding:6px 12px;font-weight:700;">${minStr}</td></tr>
          <tr><td style="padding:6px 12px;color:#666;">Max Price</td><td style="padding:6px 12px;font-weight:700;">${maxStr}</td></tr>
        </table>
      </div>`,
      type: 'warning',
      showCloseButton: true,
      showCancelButton: true,
      allowOutsideClick: false,
      customClass: {
        container: 'swal-highest-popup',
      },
    }).then((willCancel) => {
      if (!willCancel.dismiss) {
        const payload: ApplyRecommendationPayload = {
          entityType: RecommendationEntityType.ITEM,
          type: RecommendationType.PRICE,
          isSelectAll: false,
          entityKeys: [this.item.key],
          customPrices: {
            [this.item.key]: this.customPrice!
          },
          ...(this.customMin != null ? { customMinPrices: { [this.item.key]: this.customMin } } : {}),
          ...(this.customMax != null ? { customMaxPrices: { [this.item.key]: this.customMax } } : {}),
        };

        this.recommendationsService
          .apply(payload)
          .pipe(this.autoCleanUp(), first())
          .subscribe(
            () => {
              this.snotifyService.success(
                `Updated ${this.item.sku} pricing successfully`
              );
              this.item.inboundPrice = this.customPrice!; // Visual prediction
              this.item.minPrice = this.customMin ?? this.repricingItem?.newMin ?? this.item.minPrice;
              this.item.maxPrice = this.customMax ?? this.repricingItem?.newMax ?? this.item.maxPrice;
              this.activeModal.close({ isSubmitted: true });
            },
            () => {
              this.snotifyService.error(
                `Update ${this.item.sku} pricing failed`
              );
            }
          );
      }
    });
  }

  public openEdit(): void {
    this.isEditing = true;
  }

  public commitEdit(): void {
    this.isEditing = false;
  }

  public resetCustomPrice(): void {
    this.customPrice = this.repricingItem?.newInboundPrice ?? null;
    this.isEditing = false;
  }

  public getDerivedSalePrice(): number {
    return getDerivedSalePrice(this.customPrice ?? this.repricingItem?.newInboundPrice, this.repricingItem?.currentSalePrice, this.repricingItem?.currentInboundPrice);
  }

  public getEffectiveTrend(): number {
    return getRepricingEffectiveTrend(this.customPrice!, this.repricingItem?.currentInboundPrice, this.repricingItem?.trendPercentage, this.repricingItem?.newInboundPrice);
  }

  getPercentage(percentage: number): number {
    return roundFloatNumber(Math.abs(percentage));
  }

  getDisplayCurrency(value?: number) {
    return value ? this.displayCurrency?.symbol + value : 'N/A';
  }

  getPriceComparisonClass(newValue: number, currentValue: number): string {
    if (newValue == null || currentValue == null) return '';
    if (newValue > currentValue) return 'text-success';
    if (newValue < currentValue) return 'text-danger';
    return '';
  }
}
